import { LoadingButton } from '@mui/lab';
import {
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { UrlParams } from '@provision/portal-routes';
import { QueryBuilderDnD } from '@react-querybuilder/dnd';
import { QueryBuilderMaterial } from '@react-querybuilder/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { FullField, QueryBuilder, RuleGroupType } from 'react-querybuilder';
import 'react-querybuilder/dist/query-builder.css';
import { DynamicForm, DynamicIcon, FormField, Tooltip } from 'src/components';
import {
  ActionType,
  BehaviorConfigOption,
  BehaviorTrigger,
  CustomRuleGroupType,
  CustomRuleType,
  MacrosFormActions,
  mapBehaviorToFormValues,
  RulesFormFields,
  RulesFormModel,
  SelectorValue,
  UIActionField,
  useBehaviors,
} from 'src/graphql';
import { useRouteParams } from 'src/utils/routing';
import {
  actionsCustomControls,
  conditionsCustomControls,
  DEFAULT_CONDITION,
  getDefaultActionFormState,
  muiComponents,
} from './rules-form-config';
import { RQB_NEW_FIELD_NAME } from './rules-form-ui-controls';
import { filterByTrigger, useRulesEngine } from './use-rules-engine';

// TODO: Fix RulesForm values typings.
type TemporaryFormType = Record<string, BehaviorConfigOption | string | number>;

interface ActionFormProps {
  context: Record<string, unknown>;
  type: ActionType;
  disabled?: boolean;
  fields: UIActionField[];
  value: RuleGroupType | TemporaryFormType;
  onChange: (value: RuleGroupType | TemporaryFormType) => void;
}

export const ActionForm = ({
  context,
  disabled,
  value,
  onChange,
  type,
  fields,
}: ActionFormProps) => {
  const [dynamicFormFields, setDynamicFormFields] = useState<FormField[]>([]);
  useEffect(() => {
    const filteredFields = fields.reduce<FormField[]>((acc, field) => {
      const { fieldOptions, dependsOn, ...action } = field as FormField & {
        dependsOn?: { attribute: string; value: string }[];
        fieldOptions?: { value: string; label: string }[];
      };
      if (!dependsOn) {
        return [...acc, action];
      }
      const isVisibleField = dependsOn.some((dep: { attribute: string; value: string }) => {
        if (dep.attribute in value) {
          const dependentField = (value as TemporaryFormType)[
            dep.attribute
          ] as BehaviorConfigOption;
          return dep.value === dependentField?.value;
        }
        return false;
      });

      if (isVisibleField) {
        return [...acc, action as FormField];
      }
      return acc;
    }, []);
    setDynamicFormFields(filteredFields);
  }, [fields, value]);

  if (type === ActionType.Data) {
    return (
      <QueryBuilder
        resetOnOperatorChange
        resetOnFieldChange
        autoSelectField
        context={context}
        disabled={disabled}
        enableMountQueryChange={false}
        enableDragAndDrop={false}
        showCombinatorsBetweenRules
        query={value as RuleGroupType}
        onQueryChange={onChange}
        fields={fields as unknown as FullField[]}
        // @ts-expect-error Overriding default library typings
        controlElements={actionsCustomControls}
      />
    );
  }

  return (
    <DynamicForm
      style={{ gap: '1rem' }}
      disabled={disabled}
      onSubmit={onChange}
      watchable
      fields={dynamicFormFields}
      value={value}
    />
  );
};

export const RulesFormContainer = styled(Stack, {
  name: 'RulesEngineContainer',
  shouldForwardProp: (prop: string) => !['hasRules', 'dense'].includes(prop),
})<{ hasRules?: boolean; dense?: boolean }>(({ hasRules, dense, theme }) => ({
  '& .ruleGroup': {
    flexDirection: 'column-reverse',
    border: 'none',
    background: 'none',

    '&[data-level="0"]': {
      padding: 0,
    },
    '&:not([data-level="0"])': {
      border: '1px solid',
      borderColor: theme.palette.custom.OV.sys.outlineVariant,
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      background: theme.palette.custom.OV.sys.surfaceBright,
    },
  },
  '& .ruleGroup-body::before': {
    content: '"WHERE"',
    display: hasRules ? 'inline-flex' : 'none',
    flexGrow: 0,
    alignSelf: 'center',
  },
  '& .ruleGroup-body': {
    /* Override the default flex layout */
    display: 'grid !important',
    /* Allow the right-hand column (the rule/subgroup) to expand as needed */
    /* Collapse the left-hand column (the combinator) to the width of the content */
    gridTemplateColumns: 'min-content auto',
    /* Keep the combinator aligned with the top of the rule/subgroup */
    alignItems: 'start',

    '& .queryBuilder-dragHandle': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  '& .rule': {
    // Empirically picked width of the rule row to prevent inputs from overlapping
    minWidth: !dense && '40rem',
  },

  /* Indent the first rule/subgroup since it has no preceding combinator */
  '& .ruleGroup-body > .rule:first-of-type:not(:only-child), & .ruleGroup-body > .ruleGroup:first-of-type:not(:only-child)':
    {
      gridColumnStart: 2,
    },
}));

export const ActionContainer = styled(Stack, { name: 'ActionContainer' })(({ theme }) => ({
  gap: theme.spacing(1),
  alignItems: 'flex-start',
  padding: theme.spacing(2, 1, 1),
  border: '1px solid',
  borderColor: theme.palette.custom.OV.sys.outlineVariant,
  borderRadius: theme.shape.borderRadius,
  background: theme.palette.custom.OV.sys.surfaceBright,

  '& .ruleGroup-header': { display: 'none' },
}));

interface RulesEngineProps {
  title: string;
  onSaveRules: (values: RulesFormModel, behaviorId?: string) => void;
  loading?: boolean;
}
export const RulesForm = ({ title, onSaveRules, loading }: RulesEngineProps) => {
  const { behaviorId: selectedBehaviorId } = useRouteParams(UrlParams.behaviorId);
  const { formValue, config } = useRulesForm(selectedBehaviorId);
  const { triggers, conditions, actionTypes, actions, timezone, queryOptions } = config;

  const queryBuilderContext = useMemo(() => {
    return { timezone, queryOptions };
  }, [timezone, queryOptions]);

  const {
    register,
    control,
    watch,
    formState: { errors, isValid, isDirty },
    setValue,
    handleSubmit,
    reset,
  } = useForm<RulesFormModel>({
    defaultValues: formValue,
  });
  useEffect(() => {
    // For update operation, not all form data may be present in cache
    // on initial render, and form appears empty. As soon as data is ready, we hydrate form.
    if (formValue && !isDirty) {
      reset(formValue, { keepDirty: true });
    }
  }, [formValue, reset, isDirty]);

  const {
    [RulesFormFields.Conditions]: rulesQuery,
    [RulesFormFields.BehaviorTrigger]: selectedTrigger,
    [RulesFormFields.Actions]: actionsQuery,
  } = watch();

  /*
   * Not all conditions are available for every trigger, so we need
     additional filtering.
   */
  const [conditionFields, setConditionFields] = useState<SelectorValue[]>(conditions);
  useEffect(() => {
    setConditionFields(conditions.filter(filterByTrigger(selectedTrigger)));
  }, [selectedTrigger, conditions]);

  const { append, remove } = useFieldArray({
    name: RulesFormFields.Actions,
    control,
  });

  const onSave = useCallback(
    (values: RulesFormModel) => {
      return onSaveRules(values, selectedBehaviorId);
    },
    [onSaveRules, selectedBehaviorId],
  );

  // TODO: Implement validation.
  const canSubmitForm = isValid && rulesQuery?.rules.length > 0 && actionsQuery.length > 0;
  const isSubmitEnabled = validateRulesFormSubmit({ rulesQuery, actionsQuery });

  return (
    <Stack p={1} gap={1}>
      <Typography variant="h4">{title}</Typography>
      <Typography variant="body2">
        Select an event type, and conditionally trigger actions based on set rules
      </Typography>
      <Divider sx={{ margin: '0.5rem 0' }} />
      <Typography variant="h6" mb={1}>
        General
      </Typography>
      <FormControl variant="standard" error={!!errors[RulesFormFields.Name]?.message}>
        <Controller
          control={control}
          name={RulesFormFields.Name}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              size="small"
              sx={{
                alignSelf: 'flex-start',
                width: '100%',
                maxWidth: '30rem',
              }}
              error={!!errors[RulesFormFields.Name]?.message}
              label="Rule Name"
              {...field}
            />
          )}
        />
      </FormControl>
      <FormControl variant="standard" error={!!errors[RulesFormFields.Name]?.message}>
        <Controller
          control={control}
          name={RulesFormFields.Description}
          render={({ field }) => (
            <TextField
              size="small"
              sx={{
                alignSelf: 'flex-start',
                width: '100%',
                maxWidth: '30rem',
              }}
              error={!!errors[RulesFormFields.Description]?.message}
              label="Description"
              {...field}
            />
          )}
        />
      </FormControl>
      <QueryBuilderDnD>
        <QueryBuilderMaterial muiComponents={muiComponents}>
          <RulesFormContainer hasRules={!!rulesQuery?.rules.length}>
            <Typography variant="h6" mt={2} mb={1}>
              Trigger
            </Typography>
            <FormControl
              sx={{ m: 1, minWidth: 120, alignSelf: 'start' }}
              size="small"
              fullWidth={false}
            >
              <Controller
                control={control}
                name={RulesFormFields.BehaviorTrigger}
                rules={{ required: true }}
                render={({ field }) => (
                  <>
                    <InputLabel>Event</InputLabel>
                    <Select
                      label="Event"
                      {...field}
                      onChange={(e) => {
                        field.onChange(e.target.value as BehaviorTrigger);
                        setValue(RulesFormFields.Conditions, {
                          ...DEFAULT_CONDITION,
                        });
                      }}
                    >
                      {triggers?.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </>
                )}
              />
            </FormControl>
            <Typography variant="h6" mt={2}>
              Conditions
            </Typography>
            <Typography variant="body2" mb={1}>
              Conditions that must be met for the action to run
            </Typography>
            <Controller
              control={control}
              name={RulesFormFields.Conditions}
              rules={{ required: true }}
              render={({ field }) => (
                <QueryBuilder
                  enableMountQueryChange={false}
                  autoSelectField
                  resetOnOperatorChange
                  resetOnFieldChange
                  context={queryBuilderContext}
                  disabled={!selectedTrigger}
                  enableDragAndDrop
                  showCombinatorsBetweenRules
                  query={field.value}
                  onQueryChange={field.onChange}
                  fields={conditionFields as unknown as FullField[]}
                  // @ts-expect-error Overriding default library typings because of custom fields structure.
                  getOperators={(field: SelectorValue) => {
                    return field?.operators || [];
                  }}
                  // @ts-expect-error Overriding default library typings because of custom fields structure.
                  controlElements={conditionsCustomControls}
                />
              )}
            />
          </RulesFormContainer>
          <Typography variant="h6" mt={2}>
            Actions
          </Typography>
          <Typography variant="body2">
            If the above conditions are met, the following actions will occur
          </Typography>
          <RulesFormContainer gap={1}>
            {actionsQuery.map((action, index) => {
              const { onChange, ...rest } = register(`actions.${index}.actionType`);
              return (
                <ActionContainer key={action.id} gap={1}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <FormControl sx={{ minWidth: 120, alignSelf: 'start' }} size="small">
                      <InputLabel>Action</InputLabel>
                      <Select
                        label="Action"
                        value={action.actionType}
                        {...rest}
                        onChange={async (e) => {
                          const actionType = e.target.value as ActionType;
                          const formState = getDefaultActionFormState(actionType);
                          // @ts-expect-error TODO: Figure out why value is never
                          setValue(`actions.${index}.actions`, formState);

                          await onChange(e);
                        }}
                      >
                        {actionTypes?.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Tooltip title="Remove this action">
                      <IconButton
                        sx={{
                          padding: 0.5,
                          borderRadius: (theme) => theme.shape.borderRadius / 8,
                        }}
                        onClick={() => remove(index)}
                      >
                        <DynamicIcon name="delete_forever" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Controller
                    control={control}
                    name={`actions.${index}.actions`}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <ActionForm
                        key={action.actionType}
                        context={queryBuilderContext}
                        type={action.actionType}
                        disabled={!selectedTrigger}
                        value={field.value}
                        onChange={(value: unknown) => {
                          // @ts-expect-error TODO: Figure out why value is never
                          setValue(`actions.${index}.actions`, value);
                        }}
                        fields={actions[action.actionType]}
                      />
                    )}
                  />
                </ActionContainer>
              );
            })}
          </RulesFormContainer>
        </QueryBuilderMaterial>
      </QueryBuilderDnD>
      <Button
        disabled={!selectedTrigger}
        sx={{ alignSelf: 'flex-start' }}
        onClick={() =>
          append({
            id: crypto.randomUUID(),
            actionType: ActionType.Data,
            actions: getDefaultActionFormState(ActionType.Data) as CustomRuleGroupType,
          })
        }
      >
        + Action
      </Button>
      {canSubmitForm ? (
        <LoadingButton
          loading={loading}
          disabled={!isSubmitEnabled}
          onClick={handleSubmit(onSave)}
          variant="contained"
          sx={{ alignSelf: 'flex-end', gap: 1 }}
        >
          <DynamicIcon name="save" />
          Save
        </LoadingButton>
      ) : null}
    </Stack>
  );
};

function useRulesForm(behaviorId?: string) {
  const config = useRulesEngine();
  const { behaviors } = useBehaviors({
    skip: !config.actionTypes.length,
  });

  return useMemo(() => {
    const behavior = behaviors.find((b) => b.id === behaviorId);

    if (!behaviorId || !behavior) {
      return {
        formValue: {
          [RulesFormFields.Name]: '',
          [RulesFormFields.Description]: '',
          [RulesFormFields.BehaviorTrigger]: '' as BehaviorTrigger,
          [RulesFormFields.Conditions]: DEFAULT_CONDITION,
          [RulesFormFields.Actions]: [] as MacrosFormActions,
        },
        config,
      };
    }

    return {
      formValue: mapBehaviorToFormValues(behavior, config),
      config,
    };
  }, [config, behaviorId, behaviors]);
}

function validateRulesFormSubmit({
  rulesQuery,
  actionsQuery,
}: {
  rulesQuery: RulesFormModel[RulesFormFields.Conditions];
  actionsQuery: RulesFormModel[RulesFormFields.Actions];
}) {
  // TODO: Expand this validation in scope of 1V-2122
  const hasNoConditions = rulesQuery?.rules.length === 0;
  const hasNoActions = actionsQuery.length === 0;

  if (hasNoConditions || hasNoActions) {
    return false;
  }

  const hasEmptyRules = rulesQuery?.rules.some((rule) => rule.id === RQB_NEW_FIELD_NAME);
  const hasEmptyActions = actionsQuery.some((action) => {
    if ('rules' in action.actions) {
      return action.actions.rules.some(
        (rule) => (rule as CustomRuleType)?.field === RQB_NEW_FIELD_NAME,
      );
    }
    return false;
  });

  return !(hasEmptyRules || hasEmptyActions);
}
